import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import AdminStatCard from '../../components/admin/AdminStatCard';
import CreditLedgerCard from '../../components/admin/CreditLedgerCard';
import FreeTrialCard from '../../components/admin/FreeTrialCard';
import { formatAdminDate, formatAdminDuration } from '../../components/admin/adminFormatting';
import MeetingStatusBadge from '../../components/admin/MeetingStatusBadge';
import RoleBadge from '../../components/admin/RoleBadge';
import StatusBadge from '../../components/admin/StatusBadge';
import ActionsMenu from '../../components/common/ActionsMenu';
import Badge from '../../components/common/Badge';
import { Button, Card, Input, PageHeader, Select } from '@copilot/ui';
import { EmptyState, Modal } from '@copilot/ui';
import Icon from '../../components/common/Icon';
import { Field } from '../../components/common/Field';
import Loader from '../../components/common/Loader';
import { USER_ROLES } from '../../constants/roles';
import { useAuth } from '../../hooks/useAuth';
import * as adminApi from '../../services/adminApi';

function AdminUserDetailsPage() {
  const { userId } = useParams();
  const { user: currentAdmin } = useAuth();

  const [detail, setDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  // Plans for the "Assign Plan" dropdown - loaded once, independently of
  // the user detail reload above, since the catalogue does not change
  // when a status/role/plan action refreshes this page.
  const [plans, setPlans] = useState([]);
  const [plansError, setPlansError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadPlans() {
      try {
        const result = await adminApi.getAdminPricingPlans();
        if (!cancelled) setPlans(result.filter((plan) => plan.is_active));
      } catch (err) {
        if (!cancelled) setPlansError(err.message || 'Unable to load plans.');
      }
    }

    loadPlans();

    return () => {
      cancelled = true;
    };
  }, []);

  // This user's own pending plan-change request, if any - reloaded
  // alongside the rest of the page (reloadToken) so approving/rejecting
  // it, or a direct assignment elsewhere, is reflected immediately.
  const [pendingRequest, setPendingRequest] = useState(null);
  const [isReviewing, setIsReviewing] = useState(false);
  const [reviewError, setReviewError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadPendingRequest() {
      try {
        const result = await adminApi.getSubscriptionChangeRequests({
          user_id: userId,
          page_size: 1,
        });
        if (!cancelled) setPendingRequest(result.items[0] || null);
      } catch (_) {
        // Best-effort, same as the plans load above - the Subscription
        // card still renders without the pending-request panel.
      }
    }

    loadPendingRequest();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, reloadToken]);

  // One generic confirm-modal driver for the four confirmable actions
  // (disable, delete, revoke sessions, role change) instead of four
  // near-identical modal blocks - see SettingsPage.jsx's single
  // logout-all modal for the pattern this mirrors.
  const [confirmAction, setConfirmAction] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  // Only the trash action collects one; the modal renders the field
  // when confirmAction.withReason is set.
  const [reason, setReason] = useState('');
  // Typed-email confirmation for the permanent delete only.
  const [confirmEmail, setConfirmEmail] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError('');

      try {
        const result = await adminApi.getAdminUser(userId);
        if (!cancelled) setDetail(result);
      } catch (err) {
        if (!cancelled) {
          if (err.status === 404) {
            setError('User not found.');
          } else if (err.status === 403) {
            setError('You do not have permission to manage users.');
          } else {
            setError(err.message || 'Unable to load user.');
          }
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [userId, reloadToken]);

  const refresh = () => setReloadToken((token) => token + 1);

  const notify = (message) => {
    setActionMessage(message);
    setTimeout(() => setActionMessage(''), 2500);
  };

  const closeConfirm = () => {
    setConfirmAction(null);
    setReason('');
  };

  const runConfirmedAction = async () => {
    if (!confirmAction) return;

    setIsSubmitting(true);
    setActionError('');

    try {
      await confirmAction.run(reason.trim(), confirmEmail.trim());
      closeConfirm();
      if (confirmAction.afterRun) {
        // The record no longer exists, so refreshing it would 404.
        confirmAction.afterRun();
      } else {
        notify(confirmAction.successMessage);
        refresh();
      }
    } catch (err) {
      setActionError(err.message || 'Action failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRestore = async () => {
    setActionError('');
    setIsSubmitting(true);

    try {
      await adminApi.restoreAdminUser(userId);
      notify('User restored.');
      refresh();
    } catch (err) {
      setActionError(err.message || 'Unable to restore user.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEnable = async () => {
    setActionError('');
    setIsSubmitting(true);

    try {
      await adminApi.updateAdminUserStatus(userId, { is_active: true });
      notify('User enabled.');
      refresh();
    } catch (err) {
      setActionError(err.message || 'Unable to enable user.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApproveRequest = async () => {
    if (!pendingRequest) return;

    setReviewError('');
    setIsReviewing(true);

    try {
      await adminApi.approveSubscriptionChangeRequest(pendingRequest.id);
      notify('Plan change approved.');
      // Refreshes both the subscription (now-changed) and the pending
      // request (now cleared) together - refresh() is the same reload
      // this page already uses for every other action.
      refresh();
    } catch (err) {
      setReviewError(err.message || 'Unable to approve this request.');
    } finally {
      setIsReviewing(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!pendingRequest) return;

    setReviewError('');
    setIsReviewing(true);

    try {
      await adminApi.rejectSubscriptionChangeRequest(pendingRequest.id);
      notify('Plan change rejected.');
      refresh();
    } catch (err) {
      setReviewError(err.message || 'Unable to reject this request.');
    } finally {
      setIsReviewing(false);
    }
  };

  if (isLoading) {
    return <Loader label="Loading user..." />;
  }

  if (error) {
    return (
      <div>
        <Link to="/admin/users" className="admin-back-link">
          <Icon name="logout" className="icon-flip" /> Back to Users
        </Link>
        <EmptyState title="Unable to load user" description={error} />
      </div>
    );
  }

  const {
    profile,
    stats,
    subscription,
    recent_meetings: recentMeetings,
    free_trial: freeTrial,
  } = detail;
  const isSelf = currentAdmin && currentAdmin.id === profile.id;
  const isDeleted = profile.status === 'deleted';

  // One option per (plan, billing cycle) a plan actually prices - a plan
  // offering both monthly and yearly gets two entries, never a plan the
  // catalogue doesn't sell that cycle for. The value packs both fields
  // since a single <select> only carries one string.
  const planOptions = plans.flatMap((plan) => {
    const options = [];
    if (plan.monthly_price !== null) {
      options.push({
        value: `${plan.id}:monthly`,
        label: `${plan.name} — Monthly ($${plan.monthly_price})`,
        planId: plan.id,
        billingCycle: 'monthly',
      });
    }
    if (plan.yearly_price !== null) {
      options.push({
        value: `${plan.id}:yearly`,
        label: `${plan.name} — Yearly ($${plan.yearly_price})`,
        planId: plan.id,
        billingCycle: 'yearly',
      });
    }
    return options;
  });
  const currentPlanValue = subscription
    ? `${subscription.plan_id}:${subscription.billing_cycle}`
    : '';

  const openDisableConfirm = () => {
    setActionError('');
    setConfirmAction({
      title: 'Disable User',
      body: `Disable ${profile.full_name}'s account? They will not be able to sign in until re-enabled.`,
      confirmLabel: 'Disable User',
      variant: 'danger',
      successMessage: 'User disabled.',
      run: () => adminApi.updateAdminUserStatus(userId, { is_active: false }),
    });
  };

  const openDeleteConfirm = () => {
    setActionError('');
    setReason('');
    setConfirmAction({
      title: 'Move user to trash?',
      body: 'Are you sure you want to move this item to trash?',
      detail: `${profile.full_name} will be signed out everywhere and unable to log in. Nothing is erased — their meetings, transcripts and usage history are preserved, and you can restore the account at any time.`,
      confirmLabel: 'Move to Trash',
      variant: 'danger',
      successMessage: 'User moved to trash.',
      withReason: true,
      run: (deleteReason) => adminApi.deleteAdminUser(userId, deleteReason),
    });
  };

  const openHardDeleteConfirm = () => {
    setActionError('');
    setReason('');
    setConfirmEmail('');
    setConfirmAction({
      title: 'Permanently delete user?',
      body: `This permanently deletes ${profile.full_name} and ALL of their data.`,
      detail:
        'Meetings, transcripts, summaries, documents, subscriptions, payments and usage history are erased from the database. This cannot be undone and the account cannot be restored.',
      confirmLabel: 'Delete Permanently',
      variant: 'danger',
      withReason: true,
      requireEmail: profile.email,
      run: (deleteReason, email) =>
        adminApi.hardDeleteAdminUser(userId, email, deleteReason),
      afterRun: () => navigate('/admin/users'),
    });
  };

  const openRevokeConfirm = () => {
    setActionError('');
    setConfirmAction({
      title: 'Revoke All Sessions?',
      body: 'Revoke all active sessions for this user? They will be signed out everywhere and required to log in again.',
      confirmLabel: 'Revoke Sessions',
      variant: 'danger',
      successMessage: 'Sessions revoked.',
      run: () => adminApi.revokeAdminUserSessions(userId),
    });
  };

  const openRoleConfirm = (newRole) => {
    if (newRole === profile.role) return;

    setActionError('');
    const newRoleLabel = newRole === USER_ROLES.ADMIN ? 'Admin' : 'User';
    setConfirmAction({
      title: `Change role to ${newRoleLabel}?`,
      body: `Change ${profile.full_name}'s role from ${
        profile.role === USER_ROLES.ADMIN ? 'Admin' : 'User'
      } to ${newRoleLabel}?`,
      confirmLabel: 'Change Role',
      variant: 'primary',
      successMessage: 'Role updated.',
      run: () => adminApi.updateAdminUserRole(userId, { role: newRole }),
    });
  };

  const openPlanConfirm = (value) => {
    // A pending request must be approved/rejected first - the backend
    // enforces this too (PLAN_CHANGE_REQUEST_PENDING), this just avoids
    // an admin filling out the whole confirm dialog for a call that
    // would refuse anyway.
    if (!value || value === currentPlanValue || pendingRequest) return;

    const option = planOptions.find((candidate) => candidate.value === value);
    if (!option) return;

    setActionError('');
    setReason('');
    setConfirmAction({
      title: 'Change Subscription Plan?',
      body: `Assign ${profile.full_name} to the "${option.label}" plan? This replaces their current subscription immediately.`,
      confirmLabel: 'Assign Plan',
      variant: 'primary',
      successMessage: 'Plan updated.',
      withReason: true,
      reasonPlaceholder: 'Why is this plan being assigned?',
      run: (planReason) =>
        adminApi.assignUserPlan(userId, {
          plan_id: option.planId,
          billing_cycle: option.billingCycle,
          reason: planReason || null,
        }),
    });
  };

  return (
    <div>
      <Link to="/admin/users" className="admin-back-link">
        <Icon name="logout" className="icon-flip" /> Back to Users
      </Link>

      <PageHeader title={profile.full_name} description={profile.email} />

      <div className="stat-cards admin-stat-cards">
        <AdminStatCard title="Meetings" value={stats.meeting_count} icon="meeting" />
        <AdminStatCard
          title="Meeting Time"
          value={formatAdminDuration(stats.total_meeting_duration_seconds, '0 min')}
          icon="clock"
        />
        <AdminStatCard title="Transcripts" value={stats.transcript_count} icon="activity" />
        <AdminStatCard title="Questions" value={stats.question_count} icon="question" />
        <AdminStatCard title="AI Responses" value={stats.ai_response_count} icon="activity" />
        {/* Pay Per Meeting: one-time purchases, separate from the plan. */}
        <AdminStatCard
          title="Extra meetings"
          value={`${stats.meeting_passes_available ?? 0} available`}
          description={`${stats.meeting_passes_purchased ?? 0} purchased · ${stats.meeting_passes_consumed ?? 0} used`}
          icon="meeting"
        />
      </div>

      <div className="admin-detail-grid">
        <div>
          <Card interactive title="User Information">
            <div className="admin-info-list">
              <div className="admin-info-row">
                <span className="admin-info-row-label">Name</span>
                <span className="admin-info-row-value">{profile.full_name}</span>
              </div>
              <div className="admin-info-row">
                <span className="admin-info-row-label">Email</span>
                <span className="admin-info-row-value">{profile.email}</span>
              </div>
              <div className="admin-info-row">
                <span className="admin-info-row-label">Role</span>
                <span className="admin-info-row-value">
                  <RoleBadge role={profile.role} />
                </span>
              </div>
              <div className="admin-info-row">
                <span className="admin-info-row-label">Status</span>
                <span className="admin-info-row-value">
                  <StatusBadge status={profile.status} />
                </span>
              </div>
              <div className="admin-info-row">
                <span className="admin-info-row-label">Created</span>
                <span className="admin-info-row-value">{formatAdminDate(profile.created_at)}</span>
              </div>
              {isDeleted && (
                <>
                  <div className="admin-info-row">
                    <span className="admin-info-row-label">Deleted</span>
                    <span className="admin-info-row-value">
                      {formatAdminDate(profile.deleted_at)}
                      {profile.deleted_by ? ` · by admin #${profile.deleted_by}` : ''}
                    </span>
                  </div>
                  {profile.deleted_reason && (
                    <div className="admin-info-row">
                      <span className="admin-info-row-label">Reason</span>
                      <span className="admin-info-row-value">
                        {profile.deleted_reason}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </Card>

          <Card interactive title="Subscription" className="admin-section-heading">
            <div className="admin-info-list">
              <div className="admin-info-row">
                <span className="admin-info-row-label">Plan</span>
                <span className="admin-info-row-value">
                  {subscription ? subscription.plan_name : 'No plan assigned'}
                </span>
              </div>
              {subscription && (
                <>
                  <div className="admin-info-row">
                    <span className="admin-info-row-label">Status</span>
                    <span className="admin-info-row-value">
                      <Badge variant={subscription.status === 'active' ? 'listening' : 'neutral'}>
                        {subscription.status}
                      </Badge>
                    </span>
                  </div>
                  <div className="admin-info-row">
                    <span className="admin-info-row-label">Period</span>
                    <span className="admin-info-row-value">
                      {formatAdminDate(subscription.start_date)} –{' '}
                      {formatAdminDate(subscription.end_date)}
                    </span>
                  </div>
                  <div className="admin-info-row">
                    <span className="admin-info-row-label">Meetings</span>
                    <span className="admin-info-row-value">
                      {subscription.used_meetings ?? 0} /{' '}
                      {subscription.total_meetings ?? 'Unlimited'}
                    </span>
                  </div>
                  {/* Internal usage data - minutes are not a plan limit. */}
                  <div className="admin-info-row">
                    <span className="admin-info-row-label">Minutes used</span>
                    <span className="admin-info-row-value">
                      {subscription.used_minutes}
                    </span>
                  </div>
                </>
              )}

              {pendingRequest && (
                <div className="admin-pending-request">
                  <div className="admin-info-row">
                    <span className="admin-info-row-label">Pending Change Request</span>
                    <span className="admin-info-row-value">
                      {pendingRequest.requested_plan.name}
                      {' · '}
                      {pendingRequest.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'}
                      {' · '}
                      <Badge variant="paused">{pendingRequest.status}</Badge>
                    </span>
                  </div>
                  <div className="admin-request-actions">
                    <Button
                      variant="primary"
                      onClick={handleApproveRequest}
                      disabled={isReviewing}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="danger"
                      onClick={handleRejectRequest}
                      disabled={isReviewing}
                    >
                      Reject
                    </Button>
                  </div>
                  {reviewError && <p className="admin-action-status">{reviewError}</p>}
                </div>
              )}

              {!isDeleted && !pendingRequest && (
                <div className="admin-action-row">
                  <span className="admin-info-row-label">Assign Plan</span>
                  {plansError ? (
                    <p className="admin-action-status">{plansError}</p>
                  ) : planOptions.length === 0 ? (
                    <p className="admin-action-status">No active plans available.</p>
                  ) : (
                    <Select
                      value={currentPlanValue}
                      onChange={(e) => openPlanConfirm(e.target.value)}
                      aria-label="Assign plan"
                    >
                      {!subscription && <option value="">Select a plan…</option>}
                      {planOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  )}
                </div>
              )}
            </div>
          </Card>

          {/* The one-time signup trial - beside the plan, never as one. */}
          <FreeTrialCard trial={freeTrial} />

          <Card interactive title="Recent Meetings" className="admin-section-heading">
            {recentMeetings.length === 0 ? (
              <EmptyState
                title="No meetings yet."
                description="This user hasn't created any meetings."
              />
            ) : (
              <div className="admin-recent-meetings">
                {recentMeetings.map((meeting) => (
                  <div className="admin-recent-meeting-row" key={meeting.id}>
                    <span>
                      <span className="admin-recent-meeting-title">
                        {meeting.title || `Meeting #${meeting.id}`}
                      </span>
                      <span className="admin-recent-meeting-meta">
                        {formatAdminDate(meeting.created_at)} ·{' '}
                        {formatAdminDuration(meeting.duration_seconds, '0 min')}
                      </span>
                    </span>
                    <MeetingStatusBadge status={meeting.status} />
                  </div>
                ))}
              </div>
            )}
          </Card>

          <CreditLedgerCard userId={profile.id} />
        </div>

        <div className="admin-account-column">
          <Card interactive title="Manage Account">
            <div className="admin-action-list">
              {isSelf && (
                <p className="admin-action-status">
                  You&apos;re viewing your own account — self status/role/delete actions
                  are hidden.
                </p>
              )}

              {!isDeleted && (
                <div className="admin-action-row">
                  <span className="admin-info-row-label">Role</span>
                  {isSelf ? (
                    <RoleBadge role={profile.role} />
                  ) : (
                    <Select
                      value={profile.role}
                      onChange={(e) => openRoleConfirm(e.target.value)}
                      aria-label="Change role"
                    >
                      <option value={USER_ROLES.USER}>User</option>
                      <option value={USER_ROLES.ADMIN}>Admin</option>
                    </Select>
                  )}
                </div>
              )}

              <div className="admin-action-row">
                <span className="admin-info-row-label">Actions</span>
                <ActionsMenu
                  items={[
                    !isDeleted &&
                      !isSelf &&
                      (profile.is_active
                        ? {
                            label: 'Disable User',
                            onClick: openDisableConfirm,
                            variant: 'danger',
                          }
                        : {
                            label: 'Enable User',
                            onClick: handleEnable,
                            disabled: isSubmitting,
                          }),
                    { label: 'Revoke Sessions', onClick: openRevokeConfirm },
                    isDeleted
                      ? {
                          label: isSubmitting ? 'Restoring...' : 'Restore User',
                          onClick: handleRestore,
                          disabled: isSubmitting,
                        }
                      : !isSelf && {
                          label: 'Move to Trash',
                          onClick: openDeleteConfirm,
                          variant: 'danger',
                        },
                    !isSelf && {
                      label: 'Delete Permanently',
                      onClick: openHardDeleteConfirm,
                      variant: 'danger',
                    },
                  ]}
                />
              </div>

              {actionMessage && (
                <p className="admin-action-status success">{actionMessage}</p>
              )}
            </div>
          </Card>
          <Card interactive title="Device & App Information">
            <div className="admin-info-list">
              {[
                ['Operating System', detail.device_app_information?.operating_system || '-'],
                ['OS Version', detail.device_app_information?.os_version || '-'],
                ['KrishvaQ App Version', detail.device_app_information?.app_version
                  ? `v${detail.device_app_information.app_version.replace(/^v/, '')}` : '-'],
                ['Installation Status', ({ installed: 'Installed', not_installed: 'Not Installed' })[
                  detail.device_app_information?.installation_status] || 'Unknown'],
              ].map(([label, value]) => (
                <div className="admin-info-row" key={label}>
                  <span className="admin-info-row-label">{label}</span>
                  <span className="admin-info-row-value">{value}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {confirmAction && (
        <Modal
          title={confirmAction.title}
          onClose={() => !isSubmitting && closeConfirm()}
          actions={
            <>
              <Button
                variant="secondary"
                onClick={closeConfirm}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant={confirmAction.variant}
                onClick={runConfirmedAction}
                disabled={
                  isSubmitting ||
                  (confirmAction.requireEmail &&
                    confirmEmail.trim().toLowerCase() !==
                      confirmAction.requireEmail.toLowerCase())
                }
              >
                {isSubmitting ? 'Working...' : confirmAction.confirmLabel}
              </Button>
            </>
          }
        >
          {confirmAction.body}
          {confirmAction.detail && (
            <p className="field-help">{confirmAction.detail}</p>
          )}
          {confirmAction.requireEmail && (
            <Field
              label={`Type ${confirmAction.requireEmail} to confirm`}
              htmlFor="confirm-action-email"
            >
              <Input
                id="confirm-action-email"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                autoComplete="off"
              />
            </Field>
          )}
          {confirmAction.withReason && (
            <Field label="Reason (optional)" htmlFor="confirm-action-reason">
              <Input
                id="confirm-action-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={
                  confirmAction.reasonPlaceholder ||
                  'Why is this account being removed?'
                }
                maxLength={500}
              />
            </Field>
          )}
          {actionError && (
            <p className="form-alert" style={{ marginTop: 'var(--space-3)' }}>
              {actionError}
            </p>
          )}
        </Modal>
      )}
    </div>
  );
}

export default AdminUserDetailsPage;
